import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLookups } from "@/hooks/useLookups";
import { useFetch } from "@/hooks/useFetch";
import { label } from "@/lib/format";

export const STATUS_OPTS = ["draft", "pending_approval", "approved", "in_production", "partially_dispatched", "dispatched", "closed", "short_closed", "cancelled", "rejected"];
export const OPEN_PIPELINE = ["pending_approval", "approved", "in_production", "partially_dispatched"];
const MULTI = ["status", "product_category", "transaction_type", "sales_owner"];
const SINGLE = { prefix: "all", record: "all", revived: "all" };
const TEXT = ["q", "site_ref"];
export const NOT_SET = "(not set)";

// filters live in the URL so a filtered register survives reload and can be shared
export function readFilters(sp) {
  const f = {};
  MULTI.forEach((k) => { f[k] = (sp.get(k) || "").split(",").filter(Boolean); });
  Object.entries(SINGLE).forEach(([k, d]) => { f[k] = sp.get(k) || d; });
  TEXT.forEach((k) => { f[k] = sp.get(k) || ""; });
  f.sitc_missing = sp.get("sitc_missing") === "1";
  return f;
}

export function writeFilters(sp, f) {
  const n = new URLSearchParams(sp);
  MULTI.forEach((k) => (f[k].length ? n.set(k, f[k].join(",")) : n.delete(k)));
  Object.entries(SINGLE).forEach(([k, d]) => (f[k] !== d ? n.set(k, f[k]) : n.delete(k)));
  TEXT.forEach((k) => (f[k] ? n.set(k, f[k]) : n.delete(k)));
  if (f.sitc_missing) { n.set("sitc_missing", "1"); } else { n.delete("sitc_missing"); }
  return n;
}

export function MultiSelect({ options, value, onChange, placeholder, testid, quick = [] }) {
  const [s, setS] = useState("");
  const shown = options.filter((o) => o.label.toLowerCase().includes(s.toLowerCase()));
  const flip = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const text = value.length ? (value.length === 1 ? options.find((o) => o.value === value[0])?.label || value[0] : `${value.length} selected`) : placeholder;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 text-sm" data-testid={testid}>
          <span className={`truncate ${value.length ? "" : "text-slate-500"}`}>{text}</span><ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" data-testid={`${testid}-panel`}>
        {options.length > 8 && <Input className="mb-2 h-8 text-xs" placeholder="Search…" value={s} onChange={(e) => setS(e.target.value)} data-testid={`${testid}-search`} />}
        {quick.length > 0 && <div className="mb-1 flex flex-wrap gap-1">{quick.map(([l, v]) => <button key={l} className="rounded border border-slate-200 px-2 py-0.5 text-[11px] hover:bg-slate-100" onClick={() => onChange(v)} data-testid={`${testid}-quick-${l.replace(/\s+/g, "-").toLowerCase()}`}>{l}</button>)}</div>}
        <ul className="max-h-64 overflow-y-auto">
          {shown.map((o) => (
            <li key={o.value}><label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-slate-50">
              <input type="checkbox" className="h-3.5 w-3.5 accent-sky-700" checked={value.includes(o.value)} onChange={() => flip(o.value)} data-testid={`${testid}-opt-${o.value}`} />
              <span className="truncate">{o.label}</span></label></li>
          ))}
          {!shown.length && <li className="px-1 py-2 text-xs text-slate-400">No matches</li>}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function Chips({ f, setF, names, clearAll }) {
  const chips = [];
  MULTI.forEach((k) => f[k].forEach((v) => chips.push([`${names[k]}: ${k === "status" ? label(v) : v}`, () => setF({ ...f, [k]: f[k].filter((x) => x !== v) }), `${k}-${v}`])));
  if (f.q) chips.push([`Search: ${f.q}`, () => setF({ ...f, q: "" }), "q"]);
  if (f.site_ref) chips.push([`Site ref: ${f.site_ref}`, () => setF({ ...f, site_ref: "" }), "site_ref"]);
  if (f.prefix !== "all") chips.push([`LM type: ${f.prefix}`, () => setF({ ...f, prefix: "all" }), "prefix"]);
  if (f.record !== "all") chips.push([f.record === "proj" ? "Projections" : "Confirmed only", () => setF({ ...f, record: "all" }), "record"]);
  if (f.revived !== "all") chips.push(["Revived only", () => setF({ ...f, revived: "all" }), "revived"]);
  if (f.sitc_missing) chips.push(["SITC - site ref missing", () => setF({ ...f, sitc_missing: false }), "sitc_missing"]);
  if (!chips.length) return null;
  const clear = { ...f, q: "", site_ref: "", prefix: "all", record: "all", revived: "all", sitc_missing: false, ...Object.fromEntries(MULTI.map((k) => [k, []])) };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="orders-filter-chips">
      {chips.map(([t, rm, id]) => <span key={id} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-900" data-testid={`orders-chip-${id}`}>{t}<button onClick={rm} data-testid={`orders-chip-remove-${id}`}><X className="h-3 w-3" /></button></span>)}
      <button className="text-xs text-sky-700 underline" onClick={() => clearAll(clear)} data-testid="orders-clear-all">Clear all</button>
    </div>
  );
}

const NAMES = { status: "Status", product_category: "Category", transaction_type: "Txn type", sales_owner: "Salesperson" };
const SEL = (v, set, items, testid) => (
  <Select value={v} onValueChange={set}><SelectTrigger className="bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
);

// typing stays local; the URL (and the fetch) updates after a short pause
function TextFilter({ value, onChange, ...p }) {
  const [v, setV] = useState(value);
  const t = useRef(null);
  const sent = useRef(value);
  useEffect(() => { if (value !== sent.current) { clearTimeout(t.current); sent.current = value; setV(value); } }, [value]);
  useEffect(() => () => { clearTimeout(t.current); }, []);
  const send = (x) => { clearTimeout(t.current); sent.current = x; onChange(x); };
  return <Input {...p} value={v} onBlur={() => { if (v !== sent.current) { send(v); } }} onKeyDown={(e) => { if (e.key === "Enter") { send(v); } }}
    onChange={(e) => { const x = e.target.value; setV(x); clearTimeout(t.current); t.current = setTimeout(() => send(x), 400); }} />;
}

export function LmFilters({ f, setF, patch, famTypes, drill }) {
  const { opts } = useLookups();
  const { data: people } = useFetch("/sales-people");
  const lk = (t) => [...opts(t).map((x) => ({ value: x, label: x })), { value: NOT_SET, label: NOT_SET }];
  const sales = useMemo(() => [...(people || []).map((p) => ({ value: p.name, label: p.active === false ? `${p.name} (inactive)` : p.name })), { value: NOT_SET, label: NOT_SET }], [people]);
  const statuses = STATUS_OPTS.map((s) => ({ value: s, label: label(s) }));
  const [rk, setRk] = useState(0);
  const clearAll = (c) => { patch(c); setRk((r) => r + 1); };
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 mb-2">
        <TextFilter key={`q-${rk}`} className="col-span-2 bg-white" placeholder="Search LM, SO, PO, customer, site ref…" value={f.q} onChange={(x) => patch({ q: x })} data-testid="orders-search-input" />
        <MultiSelect options={statuses} value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder={drill ? "Open pipeline statuses" : "Any status"} testid="orders-status-filter"
          quick={[["Open pipeline", OPEN_PIPELINE], ["All", []]]} />
        {SEL(f.prefix, (v) => setF({ ...f, prefix: v }), [["all", "Any LM type"], ...famTypes.map((t) => [t.prefix, `${t.prefix} — ${t.description}`])], "orders-type-filter")}
        {SEL(f.record, (v) => setF({ ...f, record: v }), [["all", "Confirmed + projections"], ["conf", "Confirmed only"], ["proj", "Projections (PO pending)"]], "orders-record-filter")}
        {SEL(f.revived, (v) => setF({ ...f, revived: v }), [["all", "Revived or not"], ["revived", "Revived only"]], "orders-revived-filter")}
        <MultiSelect options={lk("product_category")} value={f.product_category} onChange={(v) => setF({ ...f, product_category: v })} placeholder="Any category" testid="orders-filter-product_category" />
        <MultiSelect options={lk("transaction_type")} value={f.transaction_type} onChange={(v) => setF({ ...f, transaction_type: v })} placeholder="Any txn type" testid="orders-filter-transaction_type" />
        <MultiSelect options={sales} value={f.sales_owner} onChange={(v) => setF({ ...f, sales_owner: v })} placeholder="Any salesperson" testid="orders-filter-sales_owner" />
        <TextFilter key={`site-${rk}`} className="bg-white" placeholder="Site reference contains" value={f.site_ref} onChange={(x) => patch({ site_ref: x })} data-testid="orders-filter-site_ref" />
        <label className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" className="h-4 w-4 accent-amber-600" checked={f.sitc_missing} onChange={(e) => setF({ ...f, sitc_missing: e.target.checked })} data-testid="orders-filter-sitc-missing" />SITC - site ref missing</label>
      </div>
      <Chips f={f} setF={setF} names={NAMES} clearAll={clearAll} />
    </>
  );
}
